import type { DateTime } from "luxon";
import { type FC, type ReactNode, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { type Job, type Nurse, NurseFullRoleShort } from "../../utils/models";
import type { QuickAddConflictKind, ShiftWindow } from "../../utils/scheduleConflict";
import type { QuickAddConflictHint } from "../../utils/scheduleConflictCopy";
import { EditTimePopover } from "../EditTimePopover";
import { StaffAutoComplete } from "../UIToolkit/Inputs/StaffAutoComplete";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { InlineHint } from "../UIToolkit/molecules/InlineHint";
import { JobSelector } from "../UIToolkit/molecules/JobSelector";
import type { QuickAddStaffOption } from "./quickAddStaffOptions";

type Props = {
	/** One row per same-day shift for anyone already on the day, one plain row for everyone else. */
	staffOptions: QuickAddStaffOption[];
	/** The picked row; the field shows the person's name whichever of their rows it is. */
	staffOption: QuickAddStaffOption | null;
	onStaffOptionChange: (option: SystemAutoCompleteOption<Nurse> | null) => void;
	renderScheduledChip: ((option: SystemAutoCompleteOption<Nurse>) => ReactNode) | undefined;
	hint: QuickAddConflictHint | null;
	conflictKind: QuickAddConflictKind;
	/** Shown only in job mode — in role mode the shift's job is not what drives the schedule. */
	showJobPicker: boolean;
	nurseJobs: Job[];
	jobId: string | undefined;
	onJobChange: (jobId: string | undefined) => void;
	locationOptions: SystemAutoCompleteOption<string>[];
	location: string | null;
	onLocationChange: (location: string | null) => void;
	window: ShiftWindow;
	/** Changes whenever the dialog sets the times itself; the time picker keeps its own copy and remounts on it. */
	timesVersion: number;
	onTimesChange: (startTime: DateTime, endTime: DateTime) => void;
};

/** The "+" Add Staff dialog's fields: person (with their scheduled-today tag and what saving will do), job, location, times. */
export const QuickAddNurseForm: FC<Props> = ({
	staffOptions,
	staffOption,
	onStaffOptionChange,
	renderScheduledChip,
	hint,
	conflictKind,
	showJobPicker,
	nurseJobs,
	jobId,
	onJobChange,
	locationOptions,
	location,
	onLocationChange,
	window,
	timesVersion,
	onTimesChange,
}) => {
	const { t } = useTranslation();
	const nurse = staffOption ? staffOption.value : null;
	const handleTimeChange = useCallback(
		({ newStartTime, newEndTime }: { newStartTime: DateTime; newEndTime: DateTime }) => onTimesChange(newStartTime, newEndTime),
		[onTimesChange],
	);
	return (
		<>
			<StaffAutoComplete
				nurses={staffOptions}
				value={staffOption}
				onChange={(_, value) => onStaffOptionChange(value)}
				endAdornment={renderScheduledChip}
				// The scheduled chip is information about the person, so clicking it picks them.
				endAdornmentSelectsOption
				fullWidth
				autoFocus
			/>
			{hint && (
				<InlineHint tone={hint.tone} dataTestId={`quick-add-nurse-conflict-hint-${conflictKind}`}>
					{hint.message}
				</InlineHint>
			)}
			{showJobPicker && nurseJobs.length > 0 && (
				<JobSelector
					variant="dropdown"
					jobs={nurseJobs}
					selectedJobIds={jobId ? [jobId] : []}
					onSelectedJobsChanged={(selected: string[]) => onJobChange(selected[0])}
					hideAllOption
					singleSelect
					label={t("job")}
				/>
			)}
			{locationOptions.length > 0 && (
				<SystemAutoComplete
					options={locationOptions}
					value={location ? { label: location, value: location } : null}
					onChange={(_, value) => onLocationChange(value?.value ?? null)}
					textFieldProps={{ label: t("location"), placeholder: t("select.location"), required: true }}
					dataTestId="quick-add-nurse-location-input"
					fullWidth
				/>
			)}
			<EditTimePopover
				// The user's own edits never change the key, so typing never remounts the picker under them.
				key={`quick-add-times-${timesVersion}`}
				startTime={window.startTime}
				endTime={window.endTime}
				onTimeChange={handleTimeChange}
				onClose={() => {}}
				anchorEl={null}
				menuWrapper={false}
				hideActions
				disableAutoCompetePortal={false}
				role={nurse ? NurseFullRoleShort[nurse.role] : "default"}
				jobId={jobId}
			/>
		</>
	);
};
